import React, {useEffect, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Button} from 'primereact/button';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dropdown} from 'primereact/dropdown';
import {Checkbox} from 'primereact/checkbox';
import {Message} from 'primereact/message';
import {ProgressBar} from 'primereact/progressbar';
import {recruitmentApi, recruitmentItems} from '../recruitmentApi';
import axios from 'axios';
import CandidateImportHistory, {importSummary, summaryText} from './CandidateImportHistory';

export const CandidateImportComponent = ({t}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selections, setSelections] = useState([]);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);
  const canAssign = (localStorage.getItem('roles') || '').includes('role_recruitment_write');
  const canWrite = (localStorage.getItem('roles') || '').includes('role_recruitment_candidate_write');
  useEffect(() => {
    if (canAssign) recruitmentItems('/recruitment').then(setSelections).catch(e => setError(e.message));
  }, [canAssign]);
  const open = async (id, details = false) => {
    setBusy(true); setError('');
    try { setPreview((await recruitmentApi('get', `/candidate-import/${id}`)).data.data); setChoices({}); setShowDetails(details); }
    catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };
  const upload = async file => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const form = new FormData(); form.append('file', file);
      setPreview((await recruitmentApi('post', '/candidate-import', form)).data.data);
      setChoices({}); setShowDetails(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.message || e.message);
      if (e.response?.status === 409 && e.response?.data?.data?.id) {
        setPreview((await recruitmentApi('get', `/candidate-import/${e.response.data.data.id}`)).data.data); setChoices({});
      }
    } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setError('');
    try {
      await recruitmentApi('post', `/candidate-import/${preview.id}/confirm`, {rows: choices});
      await open(preview.id);
    } catch (e) { setError(e.response?.data?.detail || e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/candidate-import/template`, {responseType: 'blob', headers: {Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')}});
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url; link.download = 'jelolt-import.xlsx'; link.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true); setError('');
    try { await recruitmentApi('post', `/import-state/candidate/${preview.id}/retry`); await open(preview.id); }
    catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    if (!window.confirm(t('recruitmentImport.discardConfirm'))) return;
    setBusy(true); setError('');
    try {
      await recruitmentApi('post', `/candidate-import/${preview.id}/discard`);
      setPreview(null); setChoices({});
    } catch (e) { setError(e.response?.data?.detail || e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };
  const pending = preview && ['pending', 'processing'].includes(preview.status);
  useEffect(() => {
    if (!pending) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try { const response = await recruitmentApi('get', `/candidate-import/${preview.id}`); if (!cancelled) setPreview(response.data.data); }
      catch (e) { if (!cancelled) setError(e.response?.data?.message || e.message); }
    }, 4000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pending, preview?.id]);
  const review = preview?.status === 'review' && canWrite;
  const completed = preview && ['done', 'failed', 'discarded'].includes(preview.status);
  const summary = importSummary(preview?.rows || []);
  useEffect(() => {
    if (!review) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await recruitmentApi('post', `/candidate-import/${preview.id}/preview`, {rows: choices});
        if (!cancelled) setPreview(response.data.data);
      } catch (e) { if (!cancelled) setError(e.response?.data?.detail || e.message); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [review, preview?.id, choices]);
  const change = (row, patch) => setChoices(previous => ({...previous, [row.id]: {...previous[row.id], ...patch}}));
  const isSkipped = row => row.candidate_status === 'invalid' || (choices[row.id]?.skip ?? row.skip);
  const needsDecision = row => !isSkipped(row) && row.needs_selection && !Object.prototype.hasOwnProperty.call(choices[row.id] || {}, 'recruitment_id');
  const unresolved = preview?.rows.filter(needsDecision) || [];
  const ready = preview?.rows.filter(row => !isSkipped(row) && !needsDecision(row)) || [];
  return <>
    {error && <div className="p-my-3"><Message severity="error" text={error}/></div>}
    {busy && <ProgressBar mode="indeterminate" style={{height: 5}}/>}
    <div className="p-my-3 p-d-flex p-flex-wrap" style={{gap: 8}}>
      {canWrite && !completed && <input type="file" accept=".xlsx" ref={input} hidden onChange={e => {upload(e.target.files?.[0]); e.target.value = '';}}/>}
      {canWrite && !completed && <Button icon="pi pi-upload" label={t('recruitmentImport.upload')} disabled={busy} onClick={() => input.current?.click()}/>}
      {canWrite && <Button className="p-button-outlined" icon="pi pi-download" label={t('downloadTemplateFile')} disabled={busy} onClick={download}/>}
      {preview && <Button className="p-button-text" icon="pi pi-arrow-left" label={t('recruitmentImport.history')} disabled={busy} onClick={() => {setPreview(null); setChoices({}); setError('');}}/>}
    </div>
    {!preview && <CandidateImportHistory t={t} canWrite={canWrite} onOpen={id => open(id, true)}/>}
    {preview && <>
      <h3>{preview.filename}</h3>
      {completed && <div role="status" className="p-p-4 p-my-3" style={{background: summary.failed || preview.status === 'failed' ? '#fff4df' : preview.status === 'discarded' ? '#f1f3f5' : '#edf8ef', borderRadius: 8, border: '1px solid #c8d8cd', color: '#243b2a'}}>
        <i className={`pi ${summary.failed || preview.status === 'failed' ? 'pi-exclamation-triangle' : preview.status === 'discarded' ? 'pi-times-circle' : 'pi-check-circle'}`} style={{fontSize: '2rem'}}/>
        <h2>{t(preview.status === 'discarded' ? 'recruitmentImport.state.discarded' : summary.failed || preview.status === 'failed' ? 'recruitmentImport.completedWithErrors' : 'recruitmentImport.completed')}</h2>
        <p>{summaryText(summary, t)}</p>
        <div className="p-d-flex p-flex-wrap" style={{gap: 8}}><a className="p-button p-button-outlined" href="#/recruitment/candidate">{t('recruitmentImport.viewCandidates')}</a>
          <Button label={t('recruitmentImport.details')} icon="pi pi-list" onClick={() => setShowDetails(value => !value)}/></div>
      </div>}
      <p>{review ? t('recruitmentImport.summary', {ready: ready.length, skipped: preview.rows.filter(isSkipped).length, pending: unresolved.length}) : `${t(`recruitmentImport.state.${preview.status}`)} • ${preview.processed_rows} / ${preview.total_rows}`}</p>
      {review && unresolved.length > 0 && <Message severity="warn" text={t('recruitmentImport.decisions', {rows: unresolved.map(row => row.number).join(', ')})}/>}
      {pending && <ProgressBar value={Math.round(100 * preview.processed_rows / Math.max(1, preview.total_rows))}/>}
      {(!completed || showDetails) && <DataTable value={preview.rows} dataKey="id" paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]} responsiveLayout="scroll" emptyMessage={t('recruitmentImport.empty')}>
        <Column field="number" header="#"/>
        {review && <Column header={t('recruitmentImport.rowStatus')} body={row => isSkipped(row) ? <span>{t('recruitmentImport.skip')}</span>
          : needsDecision(row) ? <span className="p-error">{t('recruitmentImport.decisionRequired')}</span>
          : <span style={{color: 'var(--green-700, #15803d)'}}><i className="pi pi-check-circle p-mr-2"/>{t('recruitmentImport.ready')}</span>}/>}
        <Column field="name" header={t('fieldName')} body={row => row.candidate_id ? <a href={`#/recruitment/candidate/read/${row.candidate_id}`}>{row.name}</a> : row.name}/>
        <Column field="email" header={t('fieldEmail')}/>
        <Column header={t('recruitmentImport.candidate')} body={row => <>{row.status === 'failed' ? t('recruitmentImport.state.failed') : row.skipped ? t('recruitmentImport.skip') : row.candidate_status ? t(`recruitmentImport.candidateStatus.${row.candidate_status}`) : t(`recruitmentImport.state.${row.status}`)}{row.error && <div className="p-error">{row.error}</div>}{(row.message || row.reason) && <small className="p-d-block">{row.message || row.reason}</small>}</>}/>
        <Column header={t('recruitmentImport.selection')} body={row => <>
          <div>{row.resolved_recruitment_name || row.recruitment_name || t('recruitmentImport.applicationStatus.none')}</div>
          {review && row.candidate_status !== 'invalid' && (row.needs_selection || Object.prototype.hasOwnProperty.call(choices[row.id] || {}, 'recruitment_id')) && <Dropdown value={Object.prototype.hasOwnProperty.call(choices[row.id] || {}, 'recruitment_id') ? choices[row.id].recruitment_id ?? '__none__' : row.recruitment_id ?? undefined}
            options={[{label: t('recruitmentImport.applicationStatus.none'), value: '__none__'}, ...selections.map(item => ({label: item.name, value: item.id}))]}
            placeholder={t('recruitmentImport.chooseSelection')} filter disabled={busy || isSkipped(row)} style={{minWidth: 240}} onChange={e => change(row, {recruitment_id: e.value === '__none__' ? null : e.value})}/>}
        </>}/>
        <Column header={t('recruitmentImport.application')} body={row => {
          const label = row.application_status ? t(`recruitmentImport.applicationStatus.${row.application_status}`) : '—';
          return row.application_id && row.candidate_id ? <a href={`#/recruitment/candidate/read/${row.candidate_id}?tab=3`}>{label} (#{row.application_id})</a> : label;
        }}/>
        {review && <Column header={t('recruitmentImport.skip')} body={row => <Checkbox aria-label={`${t('recruitmentImport.skip')} ${row.number}`} checked={row.candidate_status === 'invalid' || (choices[row.id]?.skip ?? row.skip)} disabled={busy || row.candidate_status === 'invalid'} onChange={e => change(row, {skip: e.checked})}/>}/>}
      </DataTable>}
      <div className="p-mt-3 p-text-right">
        {review && <Button className="p-button-outlined p-button-danger p-mr-2" icon="pi pi-times" label={t('recruitmentImport.discard')} disabled={busy} onClick={discard}/>}
        {pending && canWrite && <Button className="p-button-outlined p-mr-2" icon="pi pi-replay" label={t('recruitmentImport.retry')} disabled={busy} onClick={retry}/>}
        {review ? <Button className="p-button-success" icon="pi pi-check" label={t('recruitmentImport.confirm')} disabled={busy || unresolved.length > 0} loading={busy} onClick={confirm}/>
          : <Button icon="pi pi-refresh" label={t('refresh')} disabled={busy} onClick={() => open(preview.id, showDetails)}/>}
      </div>
    </>}
  </>;
};
export default withTranslation('common')(CandidateImportComponent);
